/* eslint-disable security/detect-object-injection */
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  useNotify,
  useRecordContext,
  useTranslate,
} from 'react-admin'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {
  LinkAttachmentOut,
  OrganizationWorkflowLinkOut,
  PortalRequiredAttachmentOut,
} from '../../types/apiRequests'
import {
  deleteJson,
  getJson,
  getUrl,
  postFormData,
} from '../../providers/data'
import { themeColor } from '../../layout/theme/color'

// Nested attachments route — uses providers/data.ts helpers per the frontend.md exception for non-CRUD endpoints.
const LinkAttachmentsSection: FC = () => {
  const record = useRecordContext<OrganizationWorkflowLinkOut>()
  const translate = useTranslate()
  const notify = useNotify()
  const linkId = record?.id
  const requiredAttachments = useMemo<PortalRequiredAttachmentOut[]>(
    () => record?.required_attachments ?? [],
    [record?.required_attachments],
  )

  const [attachments, setAttachments] = useState<LinkAttachmentOut[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadRole, setUploadRole] = useState('')
  const [uploadFile, setUploadFile] = useState<File | null>(null)

  const loadAttachments = useCallback(async () => {
    if (!linkId) return
    setLoading(true)
    setError(null)
    try {
      const list = await getJson<LinkAttachmentOut[]>(
        `${getUrl()}/organization_workflow_link/${linkId}/attachments`,
      )
      setAttachments(list)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load attachments')
    } finally {
      setLoading(false)
    }
  }, [linkId])

  useEffect(() => {
    void loadAttachments()
  }, [loadAttachments])

  const filledRoles = useMemo(
    () => new Set(attachments.map((a) => a.role).filter(Boolean) as string[]),
    [attachments],
  )

  const handleUpload = async () => {
    if (!linkId || !uploadFile || !uploadRole) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', uploadFile, uploadFile.name)
      form.append('role', uploadRole)
      await postFormData<LinkAttachmentOut>(
        `${getUrl()}/organization_workflow_link/${linkId}/attachments`,
        form,
      )
      notify('ra.notification.created', { type: 'info' })
      setUploadOpen(false)
      setUploadFile(null)
      setUploadRole('')
      await loadAttachments()
    } catch (e: unknown) {
      notify(e instanceof Error ? e.message : 'Upload failed', { type: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (attId: string) => {
    if (!linkId) return
    try {
      await deleteJson(
        `${getUrl()}/organization_workflow_link/${linkId}/attachments/${attId}`,
      )
      notify('ra.notification.deleted', { type: 'info' })
      await loadAttachments()
    } catch (e: unknown) {
      notify(e instanceof Error ? e.message : 'Delete failed', { type: 'error' })
    }
  }

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const openDialogForRole = (role: string) => {
    setUploadRole(role)
    setUploadOpen(true)
  }

  if (!linkId) return null

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {translate('data.attachments', { _: 'Attachments' })}
      </Typography>
      <Typography variant="caption" sx={{ color: themeColor.slate60, display: 'block', mb: 2 }}>
        {translate('linkAttachment.help', {
          _:
            'Files that data-nodes in this workflow read at runtime. '
            + 'Each role can hold one file; re-uploading the same role replaces the previous file.',
        })}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {requiredAttachments.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {requiredAttachments.map((slot) => {
            const filled = slot.role ? filledRoles.has(slot.role) : false
            return (
              <Box
                key={slot.role}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: '8px',
                  border: `1px solid ${themeColor.slate10}`,
                  bgcolor: filled ? 'rgba(0,115,77,0.03)' : 'rgba(202,138,4,0.04)',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {slot.label || slot.role}
                  </Typography>
                  {slot.description && (
                    <Typography variant="caption" sx={{ color: themeColor.slate60 }}>
                      {slot.description}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: themeColor.slate40,
                      fontFamily: '"SF Mono", "Fira Code", monospace',
                      fontSize: 11,
                    }}
                  >
                    {slot.role}{slot.format_hint ? ` · ${slot.format_hint}` : ''}
                  </Typography>
                </Box>
                <Chip
                  label={
                    filled
                      ? translate('linkAttachment.filled', { _: 'Uploaded' })
                      : translate('linkAttachment.missing', { _: 'Missing' })
                  }
                  size="small"
                  color={filled ? 'success' : 'warning'}
                  variant={filled ? 'filled' : 'outlined'}
                />
                <Button
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => slot.role && openDialogForRole(slot.role)}
                  disabled={!slot.role}
                >
                  {translate('linkAttachment.upload', { _: 'Upload' })}
                </Button>
              </Box>
            )
          })}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: themeColor.slate60 }}>
          {translate('linkAttachment.uploadedFiles', { _: 'Uploaded files' })} ({attachments.length})
        </Typography>
        {requiredAttachments.length === 0 && (
          <Button
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => setUploadOpen(true)}
            disabled={!linkId}
          >
            {translate('linkAttachment.upload', { _: 'Upload' })}
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : attachments.length === 0 ? (
        <Typography variant="body2" sx={{ color: themeColor.slate60, fontStyle: 'italic' }}>
          {translate('linkAttachment.empty', { _: 'No attachments uploaded yet.' })}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {attachments.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.25,
                borderRadius: '6px',
                border: `1px solid ${themeColor.slate10}`,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                  {a.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.25, alignItems: 'center' }}>
                  {a.role && (
                    <Chip
                      label={a.role}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10.5,
                        fontFamily: '"SF Mono", "Fira Code", monospace',
                      }}
                    />
                  )}
                  <Typography variant="caption" sx={{ color: themeColor.slate60 }}>
                    {a.content_type} · {formatBytes(a.size_bytes)}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => void handleDelete(a.id)}
                aria-label="delete"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {translate('linkAttachment.uploadTitle', { _: 'Upload attachment' })}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {requiredAttachments.length > 0 ? (
            <TextField
              select
              required
              label={translate('linkAttachment.role', { _: 'Role' })}
              value={uploadRole}
              onChange={(e) => setUploadRole(e.target.value)}
              size="small"
              fullWidth
              helperText={translate('linkAttachment.roleHelp', {
                _: 'Which slot of the workflow this file fills.',
              })}
            >
              {requiredAttachments.map((slot) => (
                <MenuItem key={slot.role} value={slot.role ?? ''}>
                  {slot.label || slot.role}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              required
              label={translate('linkAttachment.role', { _: 'Role' })}
              value={uploadRole}
              onChange={(e) => setUploadRole(e.target.value)}
              size="small"
              fullWidth
              placeholder="source_xml"
              helperText={translate('linkAttachment.roleHelpFree', {
                _: 'No slots declared by this workflow. Enter the role manually if you know it.',
              })}
            />
          )}
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            {uploadFile ? uploadFile.name : translate('linkAttachment.chooseFile', { _: 'Choose file' })}
            <input
              type="file"
              hidden
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            />
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
            {translate('ra.action.cancel', { _: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleUpload()}
            disabled={!uploadFile || !uploadRole || uploading}
          >
            {uploading
              ? translate('linkAttachment.uploading', { _: 'Uploading...' })
              : translate('linkAttachment.upload', { _: 'Upload' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default LinkAttachmentsSection
